import { DateUtils } from '../../shared/utils/date.utils';
import {
  DestroyRef,
  inject,
  Injectable,
  OnDestroy,
  signal,
} from '@angular/core';
import { BehaviorSubject, Subject, takeUntil, tap, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type SessionMessage = { type: 'REFRESH'; startTime: number } | { type: 'END' };

@Injectable({
  providedIn: 'root',
})
export class SessionCounterService implements OnDestroy {
  #LOGOUT_LOCK_KEY = 'session-logout-lock';
  #destroyRef = inject(DestroyRef);

  #sessionChannel = new BroadcastChannel('session-sync');

  #sessionDuration = signal<number>(0);
  #sessionStartTime = signal<number>(0);

  readonly #remainingTimeSubject$ = new BehaviorSubject<number>(0);
  readonly #endSessionSubject$ = new Subject<void>();

  readonly destroySessionCounter$ = new Subject<void>();
  readonly remainingTime$ = this.#remainingTimeSubject$.asObservable();
  readonly endSession$ = this.#endSessionSubject$.asObservable();

  constructor() {
    this.#initSessionChannel();
  }

  startSessionCounter(sessionDuration: number): void {
    this.#deleteLogoutLock();
    const startTime = Date.now();

    this.#startSessionFromBroadcast(startTime, sessionDuration);

    this.#sessionChannel.postMessage({
      type: 'REFRESH',
      startTime: startTime,
    });
  }

  refreshSessionCounter(): void {
    const now = Date.now();
    this.#sessionStartTime.set(now);

    this.#sessionChannel.postMessage({
      type: 'REFRESH',
      startTime: now,
    });
  }

  endSession(): void {
    this.#endSessionSubject$.next();
    this.destroySessionCounter$.next();
  }

  endSessionSessionChannelPost(): void {
    this.#sessionChannel.postMessage({ type: 'END' });
  }

  #startSessionFromBroadcast(startTime: number, sessionDuration: number): void {
    this.destroySessionCounter$.next();

    this.#sessionDuration.set(sessionDuration);
    this.#sessionStartTime.set(startTime);

    timer(0, DateUtils.SECONDS)
      .pipe(
        tap(() => {
          const now = Date.now();
          const sessionStart = this.#sessionStartTime();
          const duration = this.#sessionDuration();

          const elapsedTime = now - sessionStart;
          const remaining = Math.max(0, duration - elapsedTime);

          this.#remainingTimeSubject$.next(remaining);
          if (remaining <= 0) {
            this.endSession();
          }
        }),
        takeUntil(this.destroySessionCounter$),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #initSessionChannel(): void {
    this.#sessionChannel.onmessage = (event: MessageEvent<SessionMessage>) => {
      const message = event.data;

      switch (message.type) {
        case 'REFRESH':
          this.#sessionStartTime.set(message.startTime);
          break;

        case 'END':
          this.endSession();
          break;
      }
    };
  }

  tryAcquireLogoutLock(): boolean {
    const key = this.#LOGOUT_LOCK_KEY;
    const now = Date.now().toString();

    const existing = localStorage.getItem(key);
    if (existing) {
      return false;
    }

    localStorage.setItem(key, now);

    return localStorage.getItem(key) === now;
  }

  #deleteLogoutLock(): void {
    localStorage.removeItem(this.#LOGOUT_LOCK_KEY);
  }

  ngOnDestroy(): void {
    this.#sessionChannel.close();
  }
}
